import { useEffect } from 'react'
import { useDebounce } from '@/hooks/ui/use-debounce'
import type { PaginationResponse } from '@/types/pagination/pagination-response'
import type { TableConfig } from '@/types/table/table-config'
import { mergeObjectToSearchParams } from '@/utils/merge-object-to-search-params'
import { TableFilter } from '../../filters/table-filter'
import { TextInput } from '../../inputs/text-input'
import { PaginationTable } from '../../pagination/pagination-table'
import { Show } from '../../show'
import { PageSubtitle } from '../../titles/page-subtitle'
import { Button } from '../../ui/button'
import { useTable } from './hooks/use-table-hook'
import { MemoizedTable } from './memoized-table'

interface TableInfoProps<T> {
  tableConfig: TableConfig<T>
  paginationData?: PaginationResponse
  withFilters?: boolean
  tableTitle?: string
  isLoading?: boolean
  isFetching?: boolean
  completeListRoutToRedirect?: string
}

export function TableInfo<T>({
  tableConfig,
  withFilters,
  tableTitle,
  completeListRoutToRedirect,
  paginationData,
  isLoading
}: TableInfoProps<T>) {
  const {
    states: { pagination, searchParams, searchText },
    actions: {
      navigateToMoreItemsPage,
      setPage,
      setLimit,
      handleSearchText,
      updatePaginationSearchParams,
      setSearchParams,
      cancelInputEnterAction,
      resetPage
    }
  } = useTable()

  const pageDebounced = useDebounce(pagination.page, 500)
  const searchTextDebounced = useDebounce(searchText, 500)

  useEffect(() => {
    mergeObjectToSearchParams(searchParams, setSearchParams, [
      ['search', searchTextDebounced]
    ])
    resetPage()
  }, [searchTextDebounced])

  useEffect(() => {
    if (withFilters) {
      updatePaginationSearchParams(pageDebounced, pagination.limit)
    }
  }, [pageDebounced])

  useEffect(() => {
    setPage(searchParams.get('page') || '1')
    setLimit(searchParams.get('limit') || '10')
  }, [searchParams])

  return (
    <div className="w-full">
      <form className="w-full">
        <div className="flex justify-between items-center">
          <PageSubtitle>{tableTitle}</PageSubtitle>
          <div>
            <Show when={completeListRoutToRedirect !== undefined}>
              <Button
                className="underline font-bold text-base px-0"
                variant={'link'}
                type="button"
                onClick={() =>
                  navigateToMoreItemsPage(completeListRoutToRedirect)
                }
              >
                Ver lista Completa
              </Button>
            </Show>
          </div>
        </div>
        <div className="flex gap-8">
          <Show when={withFilters}>
            <TextInput
              value={searchText}
              onChange={handleSearchText}
              placeholder="Pesquise"
              className="bg-input/20 h-10"
              onKeyDown={cancelInputEnterAction}
            />
            <div className="w-40">
              <TableFilter heads={tableConfig.heads} />
            </div>
          </Show>
        </div>
      </form>
      <div className="w-full">
        <div className="w-full">
          <MemoizedTable<T>
            isLoading={isLoading}
            data={tableConfig.data}
            heads={tableConfig.heads}
          />
        </div>
        <Show when={withFilters}>
          <div className="w-full mt-2">
            <PaginationTable
              setLimit={() => {}}
              page={pagination.page}
              limit={pagination.limit}
              setPage={setPage}
              totalPages={String(paginationData?.totalPages)}
              isLoading={isLoading}
            ></PaginationTable>
          </div>
        </Show>
      </div>
    </div>
  )
}
